import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const Pricing = () => {
  const [activeTab, setActiveTab] = useState('monthly');
  
  const plans = [
    {
      name: 'Basic',
      price: { monthly: 5000, yearly: 50000 },
      description: 'Perfect for small schools',
      features: [
        'Up to 200 students',
        'Web application access',
        'Basic reporting',
        'Email support',
        'Student management'
      ],
      cta: 'Get Started',
      popular: false
    },
    {
      name: 'Standard',
      price: { monthly: 12000, yearly: 120000 },
      description: 'Ideal for medium schools',
      features: [
        'Up to 500 students',
        'Web + Mobile apps',
        'Advanced reporting',
        'Priority support',
        'Parent mobile app',
        'Faculty mobile app',
        'SMS notifications'
      ],
      cta: 'Get Started',
      popular: true
    },
    {
      name: 'Premium',
      price: { monthly: 25000, yearly: 250000 },
      description: 'For large institutions',
      features: [
        'Unlimited students',
        'All features included',
        'Custom integrations',
        '24/7 phone support',
        'Advanced analytics',
        'Multi-branch support',
        'Custom branding',
        'API access'
      ],
      cta: 'Contact Sales',
      popular: false
    }
  ];

  const faqs = [
    {
      question: 'Is there a setup fee?',
      answer: 'No, there are no setup fees. You only pay the monthly subscription based on your chosen plan.'
    },
    {
      question: 'Can I upgrade or downgrade my plan?',
      answer: 'Yes, you can change your plan at any time. Changes will be reflected in your next billing cycle.'
    },
    {
      question: 'Do you provide training?',
      answer: 'Yes, we provide comprehensive training for all users including administrators, teachers, and staff.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Absolutely. We use industry-standard encryption and security measures to protect your data.'
    }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Pricing Hero */}
      <section className="page-hero">
        <div className="container">
          <motion.h1
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            Choose Your Plan
          </motion.h1>
          <motion.p
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Flexible pricing for schools of all sizes
          </motion.p>
        </div>
      </section>

      {/* Pricing Toggle */}
      <section className="pricing">
        <div className="container">
          <motion.div 
            className="pricing-toggle"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <button 
              className={activeTab === 'monthly' ? 'active' : ''}
              onClick={() => setActiveTab('monthly')}
            >
              Monthly
            </button>
            <button 
              className={activeTab === 'yearly' ? 'active' : ''}
              onClick={() => setActiveTab('yearly')}
            >
              Yearly <span className="discount">Save 17%</span>
            </button>
          </motion.div>

          <div className="pricing-grid">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                className={`pricing-card ${plan.popular ? 'featured' : ''}`}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                whileHover={{ y: -10 }}
              >
                {plan.popular && (
                  <div className="plan-badge">Most Popular</div>
                )}
                
                <div className="plan-header">
                  <h3>{plan.name}</h3>
                  <div className="price">
                    <span className="currency">₹</span>
                    <span className="amount">
                      {activeTab === 'monthly' 
                        ? plan.price.monthly.toLocaleString()
                        : plan.price.yearly.toLocaleString()
                      }
                    </span>
                    <span className="period">/{activeTab === 'monthly' ? 'month' : 'year'}</span>
                  </div>
                  <p>{plan.description}</p>
                </div>
                
                <div className="plan-features">
                  <ul>
                    {plan.features.map((feature, idx) => (
                      <motion.li
                        key={idx}
                        initial={{ x: -20, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 * idx }}
                      >
                        <i className="fas fa-check"></i> {feature}
                      </motion.li>
                    ))}
                  </ul>
                </div>
                
                <div className="plan-action">
                  <Link 
                    to="/contact" 
                    className={`btn ${plan.popular ? 'btn-primary' : 'btn-outline'}`}
                  >
                    {plan.cta}
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq">
        <div className="container">
          <motion.h2 
            className="section-title"
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Frequently Asked Questions
          </motion.h2>
          <div className="faq-grid">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                className="faq-item"
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
              >
                <div 
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <h4>{faq.question}</h4>
                  <motion.i 
                    className="fas fa-plus"
                    animate={{ rotate: openFaq === index ? 45 : 0 }}
                    transition={{ duration: 0.3 }}
                  ></motion.i>
                </div>
                <motion.div 
                  className="faq-answer"
                  initial={false}
                  animate={{ 
                    height: openFaq === index ? 'auto' : 0,
                    opacity: openFaq === index ? 1 : 0
                  }}
                  transition={{ duration: 0.3 }}
                  style={{ overflow: 'hidden' }}
                >
                  <p>{faq.answer}</p>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default Pricing;